import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

// Low-poly animals built from a handful of faceted shapes (flat shaded, coloured per vertex with
// a little per-face variation, like hand-made low-poly models). Every vertex records which body
// part it belongs to (aPart) and the joint it turns around (aPivot); the shared wildlife shader
// uses them to swing legs, lower heads, wag tails and flap wings — no skeletons, one draw call
// per species. All models face +z and stand on y = 0 (fliers are centred on the origin).
//
// Parts: 0 body (static) · 1 head + neck (lowers to graze) · 2–5 legs (diagonal pairs swing
// together) · 6 tail · 7/8 left/right wings · 9/10 rabbit front/hind legs (hopping)

let seed = 1;
const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _c = new THREE.Color();
const _c2 = new THREE.Color();

const ico = (detail = 1) => new THREE.IcosahedronGeometry(1, detail);
const cyl = (rTop, rBottom, h, seg = 6) => new THREE.CylinderGeometry(rTop, rBottom, h, seg, 1);
const cone = (r, h, seg = 5, open = false) => new THREE.ConeGeometry(r, h, seg, 1, open);
const dot = () => new THREE.TetrahedronGeometry(1, 0); // 4 faces: plenty for eyes and noses

// One shape of a model: transformed, coloured (bottom→top gradient, or colorAt), tagged.
function piece(geo, { pos = [0, 0, 0], rot = [0, 0, 0], scale = [1, 1, 1], part = 0, pivot = [0, 0, 0], top = '#888', bottom, colorAt, jitter = 0.07 } = {}) {
  const g = geo.index ? geo.toNonIndexed() : geo.clone();
  geo.dispose();
  Object.keys(g.attributes).forEach((k) => { if (k !== 'position') g.deleteAttribute(k); });
  _m.compose(new THREE.Vector3(...pos), _q.setFromEuler(_e.set(...rot)), new THREE.Vector3(...scale));
  g.applyMatrix4(_m);
  const p = g.attributes.position;
  const n = p.count;
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < n; i++) { minY = Math.min(minY, p.getY(i)); maxY = Math.max(maxY, p.getY(i)); }
  const col = new Float32Array(n * 3), parts = new Float32Array(n), piv = new Float32Array(n * 3);
  let shade = 1;
  for (let i = 0; i < n; i++) {
    if (i % 3 === 0) shade = 1 + (rnd() - 0.5) * 2 * jitter; // faceted look: each face a touch lighter/darker
    if (colorAt) colorAt(_c, p.getX(i), p.getY(i), p.getZ(i), i);
    else {
      // the lower colour only covers the underside (bellies, hooves), like real coats
      const t = maxY > minY ? (p.getY(i) - minY) / (maxY - minY) : 1;
      _c.set(bottom || top).lerp(_c2.set(top), THREE.MathUtils.smoothstep(t, 0.12, 0.5));
    }
    col[i * 3] = _c.r * shade; col[i * 3 + 1] = _c.g * shade; col[i * 3 + 2] = _c.b * shade;
    parts[i] = part;
    piv[i * 3] = pivot[0]; piv[i * 3 + 1] = pivot[1]; piv[i * 3 + 2] = pivot[2];
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aPart', new THREE.BufferAttribute(parts, 1));
  g.setAttribute('aPivot', new THREE.BufferAttribute(piv, 3));
  return g;
}

function build(pieces) {
  const g = mergeGeometries(pieces);
  pieces.forEach((p) => p.dispose());
  g.computeVertexNormals();
  g.computeBoundingSphere();
  return g;
}

// A flat wing in the XZ plane from an outline (x, z): a lighter inner panel with a darker rim.
function wing(outline, { mirror = false, inner, rim, innerScale = 0.68 }) {
  const pts = outline.map(([x, z]) => [mirror ? -x : x, z]);
  const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length, cz = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const inn = pts.map(([x, z]) => [cx + (x - cx) * innerScale, cz + (z - cz) * innerScale]);
  const pos = [], cols = [];
  const ci = new THREE.Color(inner), cr = new THREE.Color(rim);
  const v = ([x, z], c) => { pos.push(x, 0, z); cols.push(c.r, c.g, c.b); };
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    v([cx, cz], ci); v(inn[i], ci); v(inn[j], ci);
    v(inn[i], ci); v(pts[i], cr); v(pts[j], cr);
    v(inn[i], ci); v(pts[j], cr); v(inn[j], ci);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  return { geo: g, colors: cols };
}
const wingPiece = (outline, opts) => {
  const { geo, colors } = wing(outline, opts);
  return piece(geo, { part: opts.part, pivot: opts.pivot, colorAt: (c, x, y, z, i) => c.setRGB(colors[i * 3], colors[i * 3 + 1], colors[i * 3 + 2]), jitter: 0.04 });
};

// ---------------------------------------------------------------- deer (doe / stag), ~1.6 m long
export function makeDeer({ antlers = false } = {}) {
  seed = antlers ? 11 : 7;
  const coat = '#7a4c2a', belly = '#d8c4a0', leg = '#5e3c22', hoof = '#211913';
  const P = [];
  P.push(piece(ico(), { pos: [0, 1.0, 0], scale: [0.2, 0.24, 0.6], top: coat, bottom: belly }));
  P.push(piece(ico(), { pos: [0, 1.04, -0.36], scale: [0.21, 0.24, 0.26], top: coat, bottom: belly }));  // rump
  P.push(piece(ico(), { pos: [0, 1.03, 0.36], scale: [0.19, 0.27, 0.25], top: coat, bottom: belly }));   // chest
  P.push(piece(ico(0), { pos: [0, 1.12, -0.6], scale: [0.12, 0.12, 0.06], top: '#efe7da' }));           // white rump patch
  // neck + head turn around the shoulders when grazing
  const neck = [0, 1.0, 0.45];
  P.push(piece(cyl(0.07, 0.12, 0.62, 6), { pos: [0, 1.38, 0.62], rot: [0.55, 0, 0], part: 1, pivot: neck, top: coat, bottom: '#a07650' }));
  P.push(piece(cyl(0.035, 0.085, 0.3, 6), { pos: [0, 1.66, 0.86], rot: [Math.PI / 2 + 0.35, 0, 0], part: 1, pivot: neck, top: '#7a5232', bottom: coat }));
  P.push(piece(dot(), { pos: [0, 1.6, 1.0], scale: [0.032, 0.03, 0.03], part: 1, pivot: neck, top: '#1c1612' }));  // nose
  for (const s of [-1, 1]) {
    P.push(piece(cone(0.045, 0.17, 4), { pos: [s * 0.08, 1.77, 0.8], rot: [0, 0, -s * 0.95], part: 1, pivot: neck, top: '#5d3e25', bottom: '#b58d66' }));
    P.push(piece(dot(), { pos: [s * 0.055, 1.69, 0.9], scale: [0.016, 0.016, 0.016], part: 1, pivot: neck, top: '#0d0b0a', jitter: 0 })); // eye
  }
  if (antlers) {
    for (const s of [-1, 1]) {
      const bone = { part: 1, pivot: neck, top: '#d8c9a8', bottom: '#7d6a4f' };
      P.push(piece(cyl(0.012, 0.022, 0.44, 4), { pos: [s * 0.1, 1.96, 0.76], rot: [-0.3, 0, -s * 0.45], ...bone }));
      P.push(piece(cyl(0.008, 0.014, 0.2, 4), { pos: [s * 0.13, 2.0, 0.86], rot: [0.5, 0, -s * 0.2], ...bone }));
      P.push(piece(cyl(0.007, 0.012, 0.18, 4), { pos: [s * 0.2, 2.1, 0.7], rot: [-0.1, 0, -s * 0.85], ...bone }));
      P.push(piece(cyl(0.006, 0.011, 0.15, 4), { pos: [s * 0.15, 2.17, 0.66], rot: [-0.35, 0, -s * 0.2], ...bone }));
    }
  }
  // legs swing from the hips/shoulders; diagonal pairs (2+5, 3+4) move together
  [[1, 0.4, 2], [-1, 0.4, 3], [1, -0.4, 4], [-1, -0.4, 5]].forEach(([s, z, part]) => {
    const pivot = [s * 0.11, 0.97, z];
    P.push(piece(cyl(0.05, 0.026, 0.97, 5), { pos: [s * 0.11, 0.485, z], part, pivot, top: leg, bottom: hoof }));
    if (z < 0) P.push(piece(ico(), { pos: [s * 0.1, 0.86, z - 0.02], scale: [0.075, 0.2, 0.12], part, pivot, top: coat, bottom: leg })); // thigh
  });
  P.push(piece(cone(0.05, 0.15, 4), { pos: [0, 1.12, -0.66], rot: [-2.2, 0, 0], part: 6, pivot: [0, 1.16, -0.6], top: '#f3efe7', bottom: '#6b4529' }));
  return build(P);
}

// ---------------------------------------------------------------- rabbit / snowshoe hare, ~0.4 m
// Coloured in light greys: the instance colour tints it (brown rabbit, white winter hare).
export function makeRabbit() {
  seed = 23;
  const back = '#c4c4c4', belly = '#ffffff';
  const P = [];
  P.push(piece(ico(), { pos: [0, 0.16, 0], rot: [0.25, 0, 0], scale: [0.1, 0.11, 0.17], top: back, bottom: belly }));
  const neck = [0, 0.22, 0.1];
  P.push(piece(ico(), { pos: [0, 0.27, 0.15], scale: [0.065, 0.07, 0.085], part: 1, pivot: neck, top: back, bottom: belly }));
  P.push(piece(dot(), { pos: [0, 0.25, 0.23], scale: [0.015, 0.012, 0.012], part: 1, pivot: neck, top: '#7a5a5a' }));  // nose
  for (const s of [-1, 1]) {
    P.push(piece(ico(0), { pos: [s * 0.028, 0.39, 0.11], rot: [-0.35, 0, -s * 0.18], scale: [0.03, 0.1, 0.014], part: 1, pivot: neck, top: '#6e6e6e', bottom: back }));
    P.push(piece(dot(), { pos: [s * 0.048, 0.29, 0.19], scale: [0.011, 0.011, 0.011], part: 1, pivot: neck, top: '#111', jitter: 0 }));
    const hip = [s * 0.07, 0.17, -0.06];
    P.push(piece(ico(), { pos: [s * 0.07, 0.11, -0.08], scale: [0.045, 0.07, 0.1], part: 10, pivot: hip, top: back, bottom: back }));
    P.push(piece(ico(0), { pos: [s * 0.07, 0.018, -0.03], scale: [0.025, 0.018, 0.085], part: 10, pivot: hip, top: belly }));
    P.push(piece(cyl(0.015, 0.012, 0.13, 4), { pos: [s * 0.04, 0.065, 0.12], part: 9, pivot: [s * 0.04, 0.13, 0.12], top: back, bottom: belly }));
  }
  P.push(piece(ico(0), { pos: [0, 0.19, -0.18], scale: [0.035, 0.035, 0.035], part: 6, pivot: [0, 0.19, -0.16], top: belly }));
  return build(P);
}

// ---------------------------------------------------------------- red fox, ~1 m with the tail
export function makeFox() {
  seed = 31;
  const fur = '#c0602a', cream = '#f1e9dc', sock = '#1e1611';
  const P = [];
  P.push(piece(ico(), { pos: [0, 0.42, 0], scale: [0.12, 0.13, 0.38], top: fur, bottom: cream }));
  P.push(piece(ico(), { pos: [0, 0.43, 0.28], scale: [0.115, 0.145, 0.13], top: fur, bottom: cream }));
  const neck = [0, 0.47, 0.36];
  P.push(piece(ico(), { pos: [0, 0.55, 0.47], scale: [0.085, 0.08, 0.095], part: 1, pivot: neck, top: fur, bottom: cream }));
  P.push(piece(cone(0.042, 0.17, 5), { pos: [0, 0.52, 0.61], rot: [Math.PI / 2, 0, 0], part: 1, pivot: neck, top: fur, bottom: cream }));
  P.push(piece(dot(), { pos: [0, 0.52, 0.695], scale: [0.017, 0.017, 0.017], part: 1, pivot: neck, top: '#121212' }));
  for (const s of [-1, 1]) {
    P.push(piece(cone(0.04, 0.12, 3), { pos: [s * 0.05, 0.66, 0.45], rot: [-0.15, 0, -s * 0.3], part: 1, pivot: neck, top: sock, bottom: fur }));
    P.push(piece(dot(), { pos: [s * 0.04, 0.575, 0.55], scale: [0.011, 0.011, 0.011], part: 1, pivot: neck, top: '#1a120a', jitter: 0 }));
  }
  [[1, 0.26, 2], [-1, 0.26, 3], [1, -0.27, 4], [-1, -0.27, 5]].forEach(([s, z, part]) => {
    P.push(piece(cyl(0.026, 0.018, 0.37, 5), { pos: [s * 0.06, 0.185, z], part, pivot: [s * 0.06, 0.37, z], top: '#8f431d', bottom: sock }));
  });
  const base = [0, 0.45, -0.36];
  P.push(piece(ico(), { pos: [0, 0.41, -0.58], rot: [-0.4, 0, 0], scale: [0.075, 0.075, 0.27], part: 6, pivot: base, top: fur, bottom: '#a5502a' }));
  P.push(piece(ico(0), { pos: [0, 0.32, -0.82], scale: [0.055, 0.055, 0.07], part: 6, pivot: base, top: cream }));
  return build(P);
}

// ---------------------------------------------------------------- bird (songbird / raven)
// Light greys tinted per instance. Wings flap around the shoulders (parts 7 and 8) and fold along
// the back when it lands; the head (part 1) pecks.
export function makeBird() {
  seed = 41;
  const P = [];
  P.push(piece(ico(), { pos: [0, 0, 0], scale: [0.045, 0.042, 0.1], top: '#9a9a9a', bottom: '#eeeeee' }));
  const neck = [0, 0.02, 0.06]; // the head pecks when the bird has landed
  P.push(piece(ico(0), { pos: [0, 0.022, 0.09], scale: [0.034, 0.034, 0.036], part: 1, pivot: neck, top: '#8a8a8a', bottom: '#e0e0e0' }));
  P.push(piece(cone(0.012, 0.04, 4), { pos: [0, 0.016, 0.135], rot: [Math.PI / 2, 0, 0], part: 1, pivot: neck, top: '#3a3226' }));
  const tail = wing([[0.006, -0.08], [0.035, -0.17], [-0.035, -0.17], [-0.006, -0.08]], { inner: '#9a9a9a', rim: '#6a6a6a', innerScale: 0.5 });
  P.push(piece(tail.geo, { colorAt: (c, x, y, z, i) => c.setRGB(tail.colors[i * 3], tail.colors[i * 3 + 1], tail.colors[i * 3 + 2]) }));
  const outline = [[0.02, 0.04], [0.1, 0.034], [0.2, 0.0], [0.245, -0.045], [0.13, -0.066], [0.02, -0.05]];
  P.push(wingPiece(outline, { part: 7, pivot: [0.02, 0.012, 0], inner: '#b5b5b5', rim: '#5c5c5c' }));
  P.push(wingPiece(outline, { mirror: true, part: 8, pivot: [-0.02, 0.012, 0], inner: '#b5b5b5', rim: '#5c5c5c' }));
  // lift the wings to the top of the body
  return build(P.map((g, i) => (i >= 4 ? g.translate(0, 0.012, 0) : g)));
}

// ---------------------------------------------------------------- butterfly (fore + hind wings)
// White wings with a dark rim; the instance colour turns them orange (monarch), yellow, blue, white.
export function makeButterfly() {
  seed = 53;
  const P = [];
  P.push(piece(cyl(0.006, 0.004, 0.05, 4), { rot: [Math.PI / 2, 0, 0], top: '#2a2a2a' }));
  const fore = [[0.004, 0.004], [0.03, 0.03], [0.062, 0.034], [0.056, 0.004], [0.004, -0.004]];
  const hind = [[0.004, -0.002], [0.046, -0.012], [0.04, -0.036], [0.015, -0.042], [0.004, -0.014]];
  for (const [outline, inner] of [[fore, '#ffffff'], [hind, '#f2f2f2']]) {
    P.push(wingPiece(outline, { part: 7, pivot: [0.004, 0, 0], inner, rim: '#1c1c1c' }));
    P.push(wingPiece(outline, { mirror: true, part: 8, pivot: [-0.004, 0, 0], inner, rim: '#1c1c1c' }));
  }
  return build(P);
}

// ---------------------------------------------------------------- wildflower clump
export function makeFlowerClump(variant = 0) {
  seed = 61 + variant * 13;
  const palette = ['#f4d23c', '#ffffff', '#a46ad6', '#e64a3c', '#f39cc4', '#f6a531'];
  const P = [];
  for (let i = 0; i < 14; i++) {
    const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * 0.6;
    const x = Math.cos(a) * r, z = Math.sin(a) * r, h = 0.1 + rnd() * 0.22;
    P.push(piece(new THREE.CylinderGeometry(0.003, 0.005, h, 3, 1, true), { pos: [x, h / 2, z], top: '#4f7a2a', bottom: '#2f5220' }));
    const col = palette[Math.floor(rnd() * palette.length)];
    P.push(piece(new THREE.CircleGeometry(0.03 + rnd() * 0.022, 5), { pos: [x, h, z], rot: [-Math.PI / 2 + (rnd() - 0.5) * 0.7, 0, rnd() * 3], top: col, jitter: 0.12 }));
    if (i % 2 === 0) P.push(piece(cone(0.02, 0.09, 3, true), { pos: [x + 0.02, 0.04, z], rot: [0.3 * (rnd() - 0.5), rnd() * 3, 0.5], top: '#5b8a2e', bottom: '#2f5220' }));
  }
  return build(P);
}

// ---------------------------------------------------------------- owl (a secret night visitor)
// The head (part 6, the "tail" motion: it swivels and bobs) turns on its neck.
export function makeOwl() {
  seed = 71;
  const back = '#6b5a48', belly = '#d9cbb2';
  const P = [];
  P.push(piece(ico(), { pos: [0, 0.2, 0], scale: [0.13, 0.19, 0.12], top: back, bottom: belly, jitter: 0.12 }));
  for (const s of [-1, 1]) {
    P.push(piece(ico(), { pos: [s * 0.1, 0.2, -0.01], rot: [0, 0, s * 0.15], scale: [0.04, 0.15, 0.09], top: '#5a4a3a', bottom: '#4a3c2f' })); // folded wings
    P.push(piece(cone(0.012, 0.05, 3), { pos: [s * 0.04, 0.0, 0.04], rot: [0.4, 0, 0], top: '#3a3026' })); // talons
  }
  const neck = [0, 0.34, 0];
  P.push(piece(ico(), { pos: [0, 0.42, 0.01], scale: [0.12, 0.1, 0.1], part: 6, pivot: neck, top: back, bottom: '#b8a78a' }));
  P.push(piece(new THREE.CircleGeometry(0.1, 12), { pos: [0, 0.41, 0.095], scale: [1.1, 0.8, 1], part: 6, pivot: neck, top: '#e3d6bd', jitter: 0.03 })); // facial disc
  for (const s of [-1, 1]) {
    P.push(piece(new THREE.CircleGeometry(0.03, 10), { pos: [s * 0.045, 0.425, 0.1], part: 6, pivot: neck, top: '#f2b705', jitter: 0 }));
    P.push(piece(new THREE.CircleGeometry(0.014, 8), { pos: [s * 0.045, 0.425, 0.102], part: 6, pivot: neck, top: '#0d0b0a', jitter: 0 }));
    P.push(piece(cone(0.025, 0.07, 3), { pos: [s * 0.07, 0.52, 0], rot: [0, 0, -s * 0.35], part: 6, pivot: neck, top: '#4a3c2f', bottom: back })); // ear tufts
  }
  P.push(piece(cone(0.012, 0.035, 4), { pos: [0, 0.39, 0.11], rot: [Math.PI / 2 + 0.6, 0, 0], part: 6, pivot: neck, top: '#3a3026' })); // beak
  return build(P);
}
